import {DotProgress, Icon, type IconName} from '@wandb/components';
import React, {type ReactNode, useState} from 'react';

export type AgentChatBannerVariant = 'upgrade' | 'info' | 'error';

export type AgentChatBannerAction = {
  text: string;
  onClick: () => void | Promise<void>;
};

export type AgentChatBannerProps = {
  variant: AgentChatBannerVariant;
  title: ReactNode;
  body?: ReactNode;
  actions?: AgentChatBannerAction[];
  /**
   * trailingAction is a single action pinned to the right of the copy (e.g.
   * Retry), rather than stacked below it like `actions`.
   */
  trailingAction?: AgentChatBannerAction;
  /**
   * onDismiss, when set, renders the dismiss (X) control; the banner is
   * controlled, so the parent must hide it.
   */
  onDismiss?: () => void;
};

type VariantStyle = {
  container: string;
  actionHover: string;
  icon: IconName;
};

// Colors use the core-ux CSS variables (loaded via @wandb/components) since the
// app's legacy Tailwind scale lacks the -100 hover steps and purple-200.
const VARIANT_STYLES: Record<AgentChatBannerVariant, VariantStyle> = {
  upgrade: {
    container: 'bg-[var(--purple-200)] text-[var(--purple-650)]',
    actionHover:
      'hover:bg-[var(--purple-100)] focus-visible:bg-[var(--purple-100)]',
    icon: 'crown-pro',
  },
  info: {
    container: 'bg-[var(--blue-200)] text-[var(--blue-650)]',
    actionHover:
      'hover:bg-[var(--blue-100)] focus-visible:bg-[var(--blue-100)]',
    icon: 'lightbulb-info',
  },
  error: {
    container: 'bg-[var(--red-200)] text-[var(--red-650)]',
    actionHover: 'hover:bg-[var(--red-100)] focus-visible:bg-[var(--red-100)]',
    icon: 'warning',
  },
};

/**
 * AgentChatBanner is a presentational notice for the agent chat panel. The
 * caller supplies the variant, copy, and any actions; deciding which banner to
 * show for a given state is the consumer's job.
 */
// TODO(WB-35541): adopt the @wandb/components Banner once it supports a purple
// `upgrade` variant with the crown-pro icon and stacked actions. It is
// inline-actions-only with a variant-fixed icon today, so this stays bespoke.
export function AgentChatBanner({
  variant,
  title,
  body,
  actions,
  trailingAction,
  onDismiss,
}: AgentChatBannerProps): JSX.Element {
  const style = VARIANT_STYLES[variant];
  // Errors announce assertively (role alert); other variants politely (status).
  const role = variant === 'error' ? 'alert' : 'status';
  // The text of the action whose async onClick is in flight, so it can show a
  // loader (e.g. Retry while billing re-pulls).
  const [pendingAction, setPendingAction] = useState<string | null>(null);

  const runAction = (action: AgentChatBannerAction) => {
    const result = action.onClick();
    if (!(result instanceof Promise)) {
      return;
    }

    setPendingAction(action.text);
    void result.finally(() => setPendingAction(null)).catch(() => {});
  };

  const renderAction = (action: AgentChatBannerAction): JSX.Element => {
    const isPending = pendingAction === action.text;
    return (
      <button
        key={action.text}
        type="button"
        onClick={() => runAction(action)}
        disabled={isPending}
        // Skip the hover tint while the loader shows, so it stays the resting
        // color and isn't hoverable.
        className={`relative rounded px-6 py-2 font-semibold ${isPending ? '' : style.actionHover}`}>
        <span className={isPending ? 'invisible' : undefined}>
          {action.text}
        </span>
        {isPending && (
          // Overlay the loader on the hidden label so the button keeps its
          // size; DotProgress inherits the button's text color.
          <span className="absolute inset-0 flex items-center justify-center">
            <DotProgress size="sm" aria-label={`${action.text} in progress`} />
          </span>
        )}
      </button>
    );
  };

  return (
    <div
      role={role}
      className={`flex items-center gap-12 rounded-lg py-6 pl-8 pr-12 text-[15px] leading-snug ${style.container}`}>
      <span className="shrink-0">
        <Icon icon={style.icon} color="currentcolor" />
      </span>
      <div className="flex flex-1 flex-col gap-8">
        <div>
          <span className="font-semibold">{title}</span>
          {body != null && <span> {body}</span>}
        </div>
        {actions != null && actions.length > 0 && (
          // Negative margin offsets the first chip's padding so its label still
          // aligns under the body text.
          <div className="-ml-6 flex gap-4">{actions.map(renderAction)}</div>
        )}
      </div>
      {trailingAction != null && (
        // Right-pinned action sharing the dismiss slot, so a single CTA sits
        // beside the copy instead of below it.
        <span className="shrink-0">{renderAction(trailingAction)}</span>
      )}
      {onDismiss != null && (
        <button
          type="button"
          aria-label="Dismiss"
          onClick={onDismiss}
          className="shrink-0">
          <Icon icon="close" color="currentcolor" />
        </button>
      )}
    </div>
  );
}
